/*
 * <mwf-configurator> — messwithfilm case configurator
 * Vanilla custom element, no dependencies. Designed for the narrow product
 * info column of a Shopify product page (works from ~300px up).
 *
 * Required: a sibling <script type="application/json" data-mwf-assets> map of
 * asset name -> URL (Shopify asset_url gives every file its own versioned URL,
 * so paths cannot be derived from a base folder).
 *
 * Attributes
 *   format            "135" | "120"            which case this product is
 *   show-format       present = show the format switch (demo / landing use)
 *   property-prefix   default "properties"     name used for the hidden inputs
 *   color-order       "body-first" pour commencer par le corps (fiche produit)
 *   variant-plain     variant id used when the text field is empty
 *   variant-engraved  variant id used when the text field is filled
 *   max-chars         default 15
 *   cta-url-135       product URL — renders a CTA that carries the current
 *   cta-url-120       selection over as query params (landing-page use)
 *   cta-label         default "Personnaliser cet étui"
 *   cart-variants     JSON {"135":id,"120":id} — renders an "Ajouter au panier"
 *                     button that posts to /cart/add.js with the properties
 *                     attached. Per-format objects {"plain":id,"engraved":id}
 *                     are also accepted, for the day the engraving is charged
 *                     extra and needs its own variant.
 *   photo             image URL shown under the preview (home block)
 *
 * A child element carrying data-mwf-intro (heading, copy) is kept and moved to
 * the top of the settings column, so on the wide layout it shares that column's
 * height with the preview — which is what sizes the photo.
 *
 *   photo-alt         its alt text
 *   add-label         default "Ajouter au panier"
 *   after-add         "drawer" (default, fires mwf:added) | "cart" | "reload"
 *
 * URL parameters (read on load, written by the CTA)
 *   ?couvercle=Cacahuète&corps=Vert%20Militaire&texte=Léo&couleur=Blanc&police=Bubble
 *
 * Events
 *   mwf:change  detail = { format, lid, body, text, textColor, font, engraved }
 */
(function () {
  const PALETTE = [
    { name: "Vert Militaire", hex: "#4A5540" },
    { name: "Noir mat", hex: "#1C1C1C" },
    { name: "Noir Graphite", hex: "#35383B" },
    { name: "Blanc", hex: "#F2F0EA" },
    { name: "Chocolat", hex: "#4B3226" },
    { name: "Cacahuète", hex: "#C9A06A" },
    { name: "Melon", hex: "#F6BF8B" },
    { name: "Vert pastel", hex: "#D2DEBB" },
    { name: "Bleu pastel", hex: "#A4D0DF" },
    { name: "Lavande", hex: "#ADB4E6" }
  ];

  const FONTS = [
    { label: "Bubble", family: "'Blue Winter'", weight: 400, track: 0 },
    { label: "Helvetica", family: "Helvetica, Arial, sans-serif", weight: 700, track: 1 }
  ];

  const ANGLES = 8;
  // the render tiles are square but the case never fills them vertically;
  // these rows are the part worth showing
  const CROP_TOP = 52, CROP_H = 326, TILE = 400;
  // engraving panel (85 x 19 mm on the lid top) as a source box for the text fitter
  const PANEL_ANGLE = [357.9, 80];
  // top view
  const TOP_W = 480, TOP_H = 199, PANEL_TOP = [415.4, 92.9];
  const TOP_QUAD = [[48.4, 58.6], [431.6, 58.6], [48.4, 144.3]];

  // [topLeft, topRight, bottomLeft] of the engraving panel, per angle, in tile px
  const QUADS = {
    "135": [
      [[123.6, 72.6], [345.1, 131.6], [74.8, 83.9]],
      [[230.6, 68], [240.1, 155.8], [169.4, 68]],
      [[325.2, 83.9], [103.5, 148.2], [276.4, 72.6]],
      [[360.2, 115.9], [39.8, 115.9], [350.8, 96.6]],
      [[296.5, 148.2], [74.8, 83.9], [345.1, 131.6]],
      [[159.9, 155.8], [169.4, 68], [240.1, 155.8]],
      [[54.9, 131.6], [276.4, 72.6], [103.5, 148.2]],
      [[49.2, 96.6], [350.8, 96.6], [39.8, 115.9]]
    ],
    "120": [
      [[137, 71.3], [315.5, 117.3], [97.5, 80.4]],
      [[225.3, 67.6], [231.5, 135.3], [174.7, 67.6]],
      [[302.5, 80.4], [123.9, 129.7], [263, 71.3]],
      [[328.6, 105.4], [71.4, 105.4], [322.5, 90.5]],
      [[276.1, 129.7], [97.5, 80.4], [315.5, 117.3]],
      [[168.5, 135.3], [174.7, 67.6], [231.5, 135.3]],
      [[84.5, 117.3], [263, 71.3], [123.9, 129.7]],
      [[77.5, 90.5], [322.5, 90.5], [71.4, 105.4]]
    ]
  };

  // luminosité perçue d'une couleur hex, entre 0 et 1
  function luminance(hex) {
    const n = parseInt(hex.slice(1), 16);
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  }

  // Force des reflets ajoutés par-dessus le teintage. Voir _sheen.
  const SHEEN = 0.08;

  /* Caractères que la gravure sait tracer, relevés dans la table de la fonte
     maison (tools/glyphes-police.js du dossier MWF_Visualizer). Tout l'ASCII
     imprimable y est, ainsi que les accents latins : un client francophone doit
     pouvoir demander « Rémi » ou « Noël ». Le reste est refusé — un emoji ou un
     alphabet que la fonte ignore serait emprunté à une autre fonte à l'écran,
     donc l'aperçu montrerait ce qui ne sera pas gravé, et la commande serait
     irréalisable. */
  const GRAVABLES = new Set(Array.from(" !\"#$%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~¡¢£¤¥¦§¨©«¬®¯°±²³´¶·¸¹»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖ×ØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõö÷øùúûüýþÿŁłŒœŠšŸŽžƒˆˇ˜ẞ–—‘’‚“”„†‡•…‰‹›⁄€™−"));

  /* Le texte que porte le couvercle tant que le visiteur n'a pas tapé le sien.
     Un couvercle nu ne dit pas qu'on peut y écrire ; celui-là le dit. Il ne
     part qu'une fois, à la première frappe, et ne revient jamais : un étui
     sans texte est un choix possible, et le reproposer après un effacement
     reviendrait à défaire ce choix. */
  const EXEMPLE_TEXTE = "messwithfilm";

  /* Deux précautions qui ne se voient pas mais comptent.

     La recomposition d'abord : « é » s'écrit en Unicode soit d'un seul
     caractère, soit d'un « e » suivi d'un accent séparé — ce que produisent
     certaines dispositions de clavier et beaucoup de copier-coller. Sans elle,
     la seconde forme perd son accent alors que la fonte contient bien le « é ».

     Le découpage ensuite, qui porte sur les points de code et non sur les
     unités de la chaîne : la plupart des emoji en occupent deux, et un
     `slice` les coupe en deux. Ce qui partait alors en propriété de commande
     n'était plus du texte valide — « Bonjour » suivi de quatre pouces levés
     donnait une chaîne dont `isWellFormed()` répondait faux. */
  function filtrerGravure(t, max) {
    const gardes = Array.from(String(t == null ? "" : t).normalize("NFC"))
      .filter(c => GRAVABLES.has(c));
    return gardes.slice(0, max).join("");
  }

  const FORMAT_LABEL = { "135": "135 · 35 mm", "120": "120 · moyen format" };

  const el = (tag, cls, parent) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (parent) parent.appendChild(n);
    return n;
  };

  class MwfConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._built) return;
      this._built = true;

      this.assets = this._readAssets();
      this.mesh3d = this._read3d();
      this.imgs = {};
      this.state = {
        format: this.getAttribute("format") === "120" ? "120" : "135",
        lid: 5, body: 0, letter: 3, font: 0, text: "", angle: 0
      };
      this.maxChars = parseInt(this.getAttribute("max-chars") || "15", 10);
      this.exempleEcarte = false;
      this.labels = this._readLabels();
      this.propPrefix = this.getAttribute("property-prefix") || "properties";
      this._readParams();

      this._render();
      this._preload();
      /* Les tuiles d'abord, la scène ensuite : si la scène s'installe, elle
         recouvre le canvas ; si elle échoue, l'aperçu est déjà là. */
      this._try3d();
      if (document.fonts) {
        document.fonts.load("40px 'Blue Winter'").then(() => this.paint()).catch(() => {});
        document.fonts.ready.then(() => this.paint()).catch(() => {});
      }
    }

    // a selection handed over from another page, e.g. the home configurator
    _readParams() {
      let q;
      try { q = new URLSearchParams(window.location.search); } catch (e) { return; }
      const byName = n => {
        const i = this.colorIndex(n);
        return i < 0 ? null : i;
      };
      const lid = byName(q.get("couvercle")),
            // « boite » était le nom de ce paramètre avant que la pièce basse
            // s'appelle le corps : les liens déjà partagés doivent rester lisibles.
            body = byName(q.get("corps") || q.get("boite")),
            col = byName(q.get("couleur"));
      if (lid !== null) this.state.lid = lid;
      if (body !== null) this.state.body = body;
      if (col !== null) this.state.letter = col;
      const font = FONTS.findIndex(f => f.label.toLowerCase() === String(q.get("police") || "").trim().toLowerCase());
      if (font >= 0) this.state.font = font;
      const text = q.get("texte");
      // le paramètre d'URL ne passe pas par le champ : il est filtré ici aussi
      if (text) {
        this.state.text = filtrerGravure(text,
          parseInt(this.getAttribute("max-chars") || "15", 10));
        // un texte venu d'une autre page vaut une frappe : l'exemple a servi
        if (this.state.text) this.exempleEcarte = true;
      }
      const fmt = q.get("format");
      if (fmt === "120" || fmt === "135") this.state.format = fmt;
    }

    // the same selection, as a link to the product page
    ctaHref() {
      const v = this.value();
      const base = this.getAttribute("cta-url-" + v.format) || this.getAttribute("cta-url-135") || "";
      if (!base) return "";
      const q = new URLSearchParams();
      q.set("couvercle", v.lidKey);
      q.set("corps", v.bodyKey);
      if (v.engraved) { q.set("texte", v.text); q.set("couleur", v.textColorKey); q.set("police", v.font); }
      return base + (base.indexOf("?") < 0 ? "?" : "&") + q.toString();
    }

    _cartVariants() {
      const raw = this.getAttribute("cart-variants");
      if (!raw) return null;
      try {
        const v = JSON.parse(raw);
        return (v && (v["135"] || v["120"])) ? v : null;
      } catch (e) {
        console.warn("[mwf-configurator] cart-variants is not valid JSON", e);
        return null;
      }
    }

    // the payload the Shopify AJAX cart expects
    cartPayload() {
      const v = this.value();
      const map = this._cartVariants() || {};
      const entry = map[v.format];
      const id = (entry && typeof entry === "object")
        ? (v.engraved ? (entry.engraved || entry.plain) : (entry.plain || entry.engraved))
        : entry;
      // Les propriétés partent dans la commande : elles portent les noms stables
      // de la PALETTE, jamais les libellés traduits. Sinon la même couleur
      // arriverait sous deux noms selon la langue du client. Le panier les
      // retraduit à l'affichage (snippets/mwf-cart-text).
      const properties = { "Format": FORMAT_LABEL[v.format], "Couvercle": v.lidKey, "Corps": v.bodyKey };
      if (v.engraved) {
        properties["Texte"] = v.text;
        properties["Couleur du texte"] = v.textColorKey;
        properties["Police"] = v.font;
      }
      return { id: id, quantity: 1, properties: properties };
    }

    _add() {
      const payload = this.cartPayload();
      this.dispatchEvent(new CustomEvent("mwf:add", { bubbles: true, detail: payload }));
      if (!payload.id) {
        console.warn("[mwf-configurator] no variant id for format " + this.state.format);
        return;
      }
      const btn = this.addBtn;
      const label = btn.textContent;
      btn.disabled = true;
      btn.textContent = this.t("adding", "Ajout…");
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(payload)
      })
        .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)))
        .then(item => {
          btn.textContent = this.t("added", "Ajouté ✓");
          this.dispatchEvent(new CustomEvent("mwf:added", { bubbles: true, detail: item }));
          const after = this.getAttribute("after-add");
          if (after === "cart") { window.location.href = "/cart"; return; }
          if (after === "reload") { window.location.reload(); return; }
          setTimeout(() => { btn.textContent = label; btn.disabled = false; }, 1800);
        })
        .catch(err => {
          console.warn("[mwf-configurator] /cart/add.js failed", err);
          btn.textContent = (err && err.description) ? this.t("unavailable", "Indisponible") : this.t("error", "Erreur");
          setTimeout(() => { btn.textContent = label; btn.disabled = false; }, 2200);
        });
    }

    // Libellés d'interface, fournis par le thème (voir snippets/mwf-labels).
    // Les noms de couleurs et de formats n'en font pas partie : ils partent
    // dans les propriétés de commande et doivent rester stables d'une langue
    // à l'autre.
    _readLabels() {
      var tag = this.querySelector('script[type="application/json"][data-mwf-labels]') ||
                document.querySelector('script[type="application/json"][data-mwf-labels]');
      if (!tag) return {};
      try { return JSON.parse(tag.textContent) || {}; }
      catch (e) { console.warn("[mwf-configurator] libellés illisibles", e); return {}; }
    }

    // Le nom dans PALETTE est l'identifiant : il ne change jamais de langue et
    // c'est lui qui circule dans les URL et les ponts. Seul l'affichage est
    // traduit — y compris la valeur envoyée dans les propriétés de commande.
    colorLabel(i) {
      var c = PALETTE[i];
      if (!c) return "";
      var map = this.labels && this.labels.colors;
      var v = map && map[c.name];
      return (typeof v === "string" && v.length) ? v : c.name;
    }

    // l'inverse : d'un libellé quelconque vers l'index de la palette
    colorIndex(nameOrLabel) {
      var q = String(nameOrLabel == null ? "" : nameOrLabel).trim().toLowerCase();
      if (!q) return -1;
      for (var i = 0; i < PALETTE.length; i++) {
        if (PALETTE[i].name.toLowerCase() === q) return i;
        if (this.colorLabel(i).toLowerCase() === q) return i;
      }
      return -1;
    }

    t(key, fallback) {
      var v = this.labels && this.labels[key];
      return (typeof v === "string" && v.length) ? v : fallback;
    }

    /* La table des maillages du visualiseur 3D, si la page l'a rendue. Son
       absence n'est pas une erreur : elle veut simplement dire qu'on reste sur
       les tuiles. */
    _read3d() {
      const n = document.querySelector("script[data-mwf-3d]");
      if (!n) return null;
      try {
        const j = JSON.parse(n.textContent);
        return (j && j.pieces) ? j.pieces : null;
      } catch (e) {
        console.warn("[mwf-configurator] table 3D illisible", e);
        return null;
      }
    }

    /* Retour aux tuiles. Le canvas ayant peut-être déjà un contexte WebGL, on
       ne peut plus lui demander un contexte 2D : on le remplace donc par un
       canvas neuf, à l'identique. C'est le seul endroit où le repli coûte
       quelque chose, et il ne se produit qu'en cas d'échec réel. */
    _replierSurTuiles() {
      if (this._replie) return;
      this._replie = true;
      const vieux = this.canvas;
      if (this.scene3d) { try { this.scene3d.detruire(); } catch (e) {} this.scene3d = null; }
      if (vieux && vieux.parentNode) {
        const neuf = document.createElement("canvas");
        neuf.className = vieux.className;
        neuf.width = vieux.width; neuf.height = vieux.height;
        vieux.parentNode.replaceChild(neuf, vieux);
        this.canvas = neuf;
      }
      /* Les huit tuiles reviennent, et avec elles la vue de dessus : sans la
         rotation libre, elle redevient le seul moyen de voir le dessus. */
      this._bandeauDessus();
      this._img("top-body"); this._img("top-lid");
      this._preloadTuiles();
      this._indice();
      this.paint();
    }

    /* Tente d'installer la scène en trois dimensions sur le canvas principal.
       Tout échec est silencieux et sans conséquence : le composant continue
       d'afficher ses tuiles, qui sont exactement ce qu'il affichait avant. */
    _try3d() {
      if (this.scene3d || !this.mesh3d || !this.canvas) return;
      /* Ceinture en plus de l'ordre des scripts : si la scène n'est pas encore
         définie, on retente une fois la page chargée. Sans cela un simple
         changement d'ordre dans le thème ferait silencieusement retomber la
         boutique sur les tuiles. */
      if (!window.MwfScene3D) {
        /* Le script de la scène n'est pas encore là. On l'attend une fois — mais
           seulement si la page n'a pas fini de charger : sinon l'événement ne
           viendrait jamais, et le canvas resterait vide sans que les tuiles
           soient demandées. Ce cas se produit dès qu'un configurateur est monté
           après coup, par exemple injecté par une section. */
        if (document.readyState === "complete") { this._replierSurTuiles(); return; }
        if (this._attend3d) return;
        this._attend3d = true;
        window.addEventListener("load", () => { this._attend3d = false; this._try3d(); },
                                { once: true });
        return;
      }
      const paquets = {}, manifeste = {};
      Object.keys(this.mesh3d).forEach(cle => {
        const v = this.mesh3d[cle];
        paquets[cle] = v.url;
        manifeste[cle] = v;
      });
      const polices = FONTS.map(f => ({ famille: f.family, graisse: f.weight, track: f.track }));
      const scene = window.MwfScene3D.creer(this.canvas, { paquets, manifeste, polices });
      if (!scene) { this._replierSurTuiles(); return; }
      this.scene3d = scene;
      /* Le premier chargement décide : s'il échoue, le canvas n'a encore rien
         reçu et on revient aux tuiles sans que le visiteur ait rien vu. */
      scene.poser({
        format: this.state.format,
        corps: PALETTE[this.state.body].hex,
        couvercle: PALETTE[this.state.lid].hex,
        lettres: PALETTE[this.state.letter].hex,
        texte: this._texteAffiche(), police: this.state.font
      }).then(pret => { if (!pret) this._replierSurTuiles(); })
        .catch(() => this._replierSurTuiles());
    }

    _readAssets() {
      const tag = this.querySelector('script[type="application/json"][data-mwf-assets]') ||
        document.querySelector('script[type="application/json"][data-mwf-assets]');
      if (!tag) {
        console.warn("[mwf-configurator] no data-mwf-assets map found");
        return {};
      }
      try { return JSON.parse(tag.textContent); } catch (e) {
        console.warn("[mwf-configurator] asset map is not valid JSON", e);
        return {};
      }
    }

    _url(name) { return this.assets[name] || ""; }

    _img(name) {
      const src = this._url(name);
      if (!src) return null;
      if (!this.imgs[src]) {
        const im = new Image();
        im.onload = () => this.paint();
        im.src = src;
        this.imgs[src] = im;
      }
      return this.imgs[src];
    }

    /* La troisième dimension est-elle plausible ? Test synchrone et instantané,
       fait avant de précharger quoi que ce soit : sans lui, on téléchargeait les
       trente-deux tuiles — deux cent quatre-vingt-treize kilo-octets, mesurés —
       en plus des maillages qui les remplacent. On payait les deux. */
    _3dPlausible() {
      return !!(this.mesh3d && this.canvas && window.WebGL2RenderingContext);
    }

    _preload() {
      if (this._3dPlausible()) return;
      /* La vue de dessus n'existe qu'en repli — voir _bandeauDessus. Ses deux
         tuiles ne se chargent donc qu'avec elle. */
      this._img("top-body"); this._img("top-lid");
      this._preloadTuiles();
    }

    /* Les seize tuiles par format, avec leurs reflets. Appelé au montage quand
       la troisième dimension n'est pas plausible, et en rattrapage si elle
       échoue malgré tout — un maillage qui n'arrive pas, un shader refusé. */
    _preloadTuiles() {
      if (this._tuilesDemandees) return;
      this._tuilesDemandees = true;
      ["135", "120"].forEach(f => {
        for (let i = 0; i < ANGLES; i++) {
          this._img(f + "-body-" + i); this._img(f + "-lid-" + i);
          this._img(f + "-body-" + i + "-sheen"); this._img(f + "-lid-" + i + "-sheen");
        }
      });
    }

    /* ---------- markup ---------- */

    _render() {
      const keep = this.querySelector("script[data-mwf-assets]");
      const intro = this.querySelector("[data-mwf-intro]");
      this.innerHTML = "";
      if (keep) this.appendChild(keep);
      const root = this.root = el("div", "mwf", this);

      // DOM order is the narrow one: every setting first, then the previews.
      // The wide layout re-places these four groups with grid areas.
      const params = el("div", "mwf-params", root);
      if (intro) {
        intro.classList.add("mwf-intro");
        params.appendChild(intro);
      }

      // format switch (optional — on a product page the format IS the product)
      if (this.hasAttribute("show-format")) {
        const fs = el("div", "mwf-field", params);
        el("div", "mwf-label", fs).textContent = this.t("format", "Format");
        const row = el("div", "mwf-row", fs);
        this.formatBtns = ["135", "120"].map(fmt => {
          const b = el("button", "mwf-pill", row);
          b.type = "button";
          b.textContent = fmt;
          b.addEventListener("click", () => this._set({ format: fmt }));
          return b;
        });
      }

      // Ordre des deux rangées de couleurs. En pleine largeur l'aperçu est à
      // côté des réglages : couvercle en haut, corps en bas suit la géométrie
      // de l'objet. Sur une fiche produit où l'aperçu part dans l'autre
      // colonne, cette correspondance disparaît et on commence par le corps,
      // qui porte la variante.
      /* Pas de texte d'aide sous les deux rangées de couleurs : l'aperçu 3D
         montre le ton sur ton et le bicolore mieux qu'une phrase, et sur un
         téléphone cette phrase s'insérait entre les pastilles du couvercle et
         celles du corps — deux rangées voisines qu'elle donnait à lire comme
         deux réglages séparés. */
      const addLid = () => {
        this.lidChips = this._swatches(params, this.t("lid", "Couleur du couvercle"), "lid");
      };
      const addBody = () => {
        this.bodyChips = this._swatches(params, this.t("body", "Couleur du corps"), "body");
      };
      if (this.getAttribute("color-order") === "body-first") { addBody(); addLid(); }
      else { addLid(); addBody(); }

      // custom text
      const tf = el("div", "mwf-field", params);
      const tl = el("div", "mwf-label mwf-label--split", tf);
      el("span", null, tl).textContent = this.t("text", "Votre texte");
      this.counter = el("span", "mwf-count", tl);
      this.input = el("input", "mwf-input", tf);
      this.input.type = "text";
      this.input.maxLength = this.maxChars;
      this.input.placeholder = this.t("text_placeholder", "ex : PORTRA 400, Exposed, Shoot More Film");
      this.input.setAttribute("aria-label", this.t("text_aria", "Votre texte sur le couvercle"));
      this.input.addEventListener("input", () => {
        const avant = this.input.value;
        const apres = filtrerGravure(avant, this.maxChars);
        if (apres !== avant) {
          /* On replace le curseur là où il était, moins ce qui a disparu devant
             lui : sinon il saute en fin de champ à chaque frappe refusée. */
          const pos = this.input.selectionStart || 0;
          const retires = Array.from(avant.slice(0, pos).normalize("NFC"))
            .filter(c => !GRAVABLES.has(c)).length;
          this.input.value = apres;
          const np = Math.max(0, Math.min(apres.length, pos - retires));
          try { this.input.setSelectionRange(np, np); } catch (e) {}
        }
        // la première frappe retire l'exemple, définitivement
        if (apres) this.exempleEcarte = true;
        this._set({ text: apres });
      });

      // La limite est dite par le compteur « 8 / 15 » posé dans le libellé, et
      // tenue par maxLength ci-dessus : la répéter en dessous du champ ne fait
      // qu'éloigner la couleur du texte de son champ.
      this.letterChips = this._swatches(tf, this.t("text_color", "Couleur du texte"), "letter");
      this._help(tf, this.t("text_color_help", "Même couleur que le couvercle ? Le texte sera gravé en creux, pour un rendu discret."));

      const ff = el("div", "mwf-field", params);
      el("div", "mwf-label", ff).textContent = this.t("font", "Police du texte");
      const frow = el("div", "mwf-row", ff);
      this.fontBtns = FONTS.map((font, i) => {
        const b = el("button", "mwf-pill", frow);
        b.type = "button";
        b.textContent = font.label;
        // le bouton donne à lire la police qu'il propose
        b.setAttribute("data-mwf-font", font.label);
        b.style.fontFamily = font.family;
        b.style.fontWeight = font.weight;
        b.addEventListener("click", () => this._set({ font: i }));
        return b;
      });

      // preview + optional photo form the right-hand column of the wide layout
      const viewcol = this.viewcol = el("div", "mwf-viewcol", root);
      const view = el("div", "mwf-view", viewcol);
      view.setAttribute("role", "img");
      view.setAttribute("aria-label", this.t("preview_aria", "Aperçu de votre étui"));
      this.canvas = el("canvas", "mwf-canvas", view);
      this.canvas.width = TILE; this.canvas.height = CROP_H;
      /* La pastille est un bouton, pas seulement une étiquette : sans elle,
         voir l'étui nu obligerait à taper un caractère puis à l'effacer.
         Elle s'en va avec l'exemple qu'elle annonce, et comme lui, une fois. */
      this.badge = el("button", "mwf-exemple", view);
      this.badge.type = "button";
      this.badge.setAttribute("aria-label", this.t("example_remove", "Retirer le texte d'exemple"));
      el("span", null, this.badge).textContent = this.t("example_badge", "Exemple de texte");
      this.badge.insertAdjacentHTML("beforeend", croix());
      this.badge.addEventListener("click", () => {
        this.exempleEcarte = true;
        this._sync();
        this.paint();
      });
      /* La vue tourne au glisser : sans cette digue, appuyer sur la pastille
         amorce une rotation en même temps qu'il retire l'exemple. */
      ["mousedown", "touchstart"].forEach(type =>
        this.badge.addEventListener(type, e => e.stopPropagation()));
      this.hint = el("div", "mwf-hint", view);
      this._indice();
      view.addEventListener("mousedown", e => this._drag(e));
      view.addEventListener("touchstart", e => this._drag(e), { passive: true });

      const photo = this.getAttribute("photo");
      if (photo) {
        this.photo = el("img", "mwf-photo", viewcol);
        this.photo.src = photo;
        this.photo.alt = this.getAttribute("photo-alt") || "";
        this.photo.loading = "lazy";
      }

      this.summary = el("p", "mwf-summary", root);
      /* La vue de dessus n'a d'objet que sans la troisième dimension : elle
         existait pour montrer le seul angle que les huit tuiles n'atteignent
         pas. Dès qu'on peut tourner la pièce, elle ne montre plus rien qu'on ne
         puisse aller voir. */
      if (!this._3dPlausible()) this._bandeauDessus();

      const variants = this._cartVariants();
      if (this.getAttribute("cta-url-135") || this.getAttribute("cta-url-120") || variants) {
        const actions = el("div", "mwf-actions", root);
        if (variants) {
          this.addBtn = el("button", "mwf-cta mwf-cta--solid", actions);
          this.addBtn.type = "button";
          this.addBtn.textContent = this.getAttribute("add-label") || this.t("add", "Ajouter au panier");
          this.addBtn.addEventListener("click", () => this._add());
        }
        if (this.getAttribute("cta-url-135") || this.getAttribute("cta-url-120")) {
          this.cta = el("a", "mwf-cta mwf-cta--ghost", actions);
          this.cta.textContent = this.getAttribute("cta-label") || this.t("cta", "Personnaliser cet étui");
        }
      }

      // hidden inputs — submitted with the theme's product form
      this.fields = {};
      ["Format", "Couvercle", "Corps", "Texte", "Couleur du texte", "Police"].forEach(k => {
        const i = el("input", null, root);
        i.type = "hidden";
        i.name = this.propPrefix + "[" + k + "]";
        this.fields[k] = i;
      });
      if (this.getAttribute("variant-plain") || this.getAttribute("variant-engraved")) {
        this.variantInput = el("input", null, root);
        this.variantInput.type = "hidden";
        this.variantInput.name = "id";
      }

      this._sync();
    }

    /* La ligne sous l'aperçu.

       Elle ne nomme que les gestes dont on est sûr. Au doigt, le pincement est
       certain, donc annoncé. À la souris il n'y a plus de zoom au défilement, et
       le pincement du pavé tactile marche mais ne se détecte pas : promettre un
       pavé tactile à qui n'a qu'une souris serait pire que de se taire. */
    _indice() {
      if (!this.hint) return;
      const doigt = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
      const texte = doigt && this._3dPlausible() && !this._replie
        ? this.t("pinch_hint", "Glissez · pincez pour zoomer")
        : this.t("drag_hint", "Glissez pour tourner");
      this.hint.innerHTML = arrow(false) + '<span></span>' + arrow(true);
      this.hint.querySelector("span").textContent = texte;
    }

    /* Le bandeau « vue de dessus », construit à la demande.

       Il se place **juste après l'aperçu, où que celui-ci soit**, et non à une
       position fixe dans le composant. La nuance compte : sur la fiche produit,
       un pont du thème déplace l'aperçu sous la galerie photo au chargement. Un
       bandeau ancré à la racine du composant se retrouvait alors dans la colonne
       des réglages, loin de l'aperçu qu'il complète — visible seulement lors
       d'un repli tardif, quand le bandeau naît après le déplacement.

       Suivre l'aperçu couvre les deux cas d'un coup, et rend au bandeau la place
       exacte qu'il occupait quand il était inconditionnel : dans la colonne
       étroite il venait après l'aperçu et avant le résumé. */
    _bandeauDessus() {
      if (this.topCanvas) return;
      const ancre = this.viewcol;
      if (!ancre || !ancre.parentNode) return;
      const top = el("div", "mwf-top");
      el("div", "mwf-label mwf-label--sub", top).textContent = this.t("top_view", "Vue de dessus");
      this.topCanvas = el("canvas", "mwf-topcanvas", top);
      this.topCanvas.width = TOP_W; this.topCanvas.height = TOP_H;
      ancre.parentNode.insertBefore(top, ancre.nextSibling);
    }

    _help(parent, text) {
      if (!text) return null;
      var p = el("p", "mwf-help", parent);
      p.textContent = text;
      return p;
    }

    _swatches(parent, label, key) {
      const f = el("div", "mwf-field", parent);
      f.setAttribute("data-mwf-role", key);
      let value = null;
      if (label) {
        const l = el("div", "mwf-label mwf-label--split", f);
        el("span", null, l).textContent = label + " —";
        value = el("span", "mwf-value", l);
      }
      const row = el("div", "mwf-swatches", f);
      const btns = PALETTE.map((c, i) => {
        const b = el("button", "mwf-swatch", row);
        b.type = "button";
        b.title = this.colorLabel(i);
        b.setAttribute("aria-label", b.title);
        b.setAttribute("data-mwf-color", c.name);
        // luminosité perçue : décide de la couleur de la coche de sélection
        b.setAttribute("data-mwf-light", luminance(c.hex) > 0.62 ? "1" : "0");
        /* La couleur de la pastille **est** l'information, pas une décoration :
           sans elle il ne reste qu'un rond vide et le configurateur n'est plus
           utilisable. On la pose donc en ligne et en !important, seul niveau que
           rien ne surpasse.

           Ce n'est pas de la prudence gratuite. Un style en ligne perd contre
           une règle !important venue d'une feuille de style, et c'est exactement
           ce que font les assombrisseurs de pages — extensions de mode sombre,
           thème sombre automatique de certains navigateurs : ils appliquent
           `background-color: … !important` à tous les boutons. Mesuré sur la
           fiche produit : la pastille passait de rgb(74,85,64) à rgb(27,27,27),
           et les dix ronds devenaient identiques. En ligne et !important, la
           couleur résiste. */
        b.style.setProperty("background-color", c.hex, "important");
        b.addEventListener("click", () => this._set({ [key]: i }));
        return b;
      });
      return { btns: btns, value: value };
    }

    /* ---------- state ---------- */

    _set(patch) {
      /* Dernier rempart. La saisie et le paramètre d'URL sont déjà filtrés,
         mais _set est la seule porte par laquelle l'état change : la fermer ici
         garantit qu'aucune autre voie — un appel programmatique, une évolution
         de l'interface — ne laisse passer un caractère ingravable. */
      if (patch && typeof patch.text === "string")
        patch = Object.assign({}, patch, { text: filtrerGravure(patch.text, this.maxChars) });
      Object.assign(this.state, patch);
      this._sync();
      this.paint();
      this.dispatchEvent(new CustomEvent("mwf:change", { bubbles: true, detail: this.value() }));
    }

    value() {
      const s = this.state;
      return {
        format: s.format,
        lid: this.colorLabel(s.lid),
        body: this.colorLabel(s.body),
        lidKey: PALETTE[s.lid].name,
        bodyKey: PALETTE[s.body].name,
        textColorKey: PALETTE[s.letter].name,
        text: s.text.trim(),
        textColor: this.colorLabel(s.letter),
        font: FONTS[s.font].label,
        engraved: s.text.trim().length > 0
      };
    }

    /* Ce que l'aperçu montre — jamais ce que la commande retient. La vignette
       du panier n'a pas de champ de saisie : elle rend toujours le texte réel,
       sans quoi un étui commandé sans texte arriverait signé. */
    _texteAffiche() {
      if (!this.input || this.state.text) return this.state.text;
      return this.exempleEcarte ? "" : EXEMPLE_TEXTE;
    }

    _sync() {
      const s = this.state, v = this.value();
      if (this.formatBtns) this.formatBtns.forEach((b, i) => b.classList.toggle("is-on", ["135", "120"][i] === s.format));
      this.fontBtns.forEach((b, i) => b.classList.toggle("is-on", i === s.font));
      this.lidChips.btns.forEach((b, i) => b.classList.toggle("is-on", i === s.lid));
      this.bodyChips.btns.forEach((b, i) => b.classList.toggle("is-on", i === s.body));
      this.letterChips.btns.forEach((b, i) => b.classList.toggle("is-on", i === s.letter));
      if (this.lidChips.value) this.lidChips.value.textContent = v.lid;
      if (this.bodyChips.value) this.bodyChips.value.textContent = v.body;
      // le libellé de la couleur du texte affichait un tiret sans rien derrière
      if (this.letterChips.value) this.letterChips.value.textContent = v.textColor;
      if (this.badge) this.badge.classList.toggle("is-off", !!s.text || this.exempleEcarte);
      this.counter.textContent = s.text.length + " / " + this.maxChars;
      if (this.input.value !== s.text) this.input.value = s.text;

      // on tape dans la police qu'on a choisie
      const face = FONTS[s.font];
      if (face) {
        this.input.setAttribute("data-mwf-font", face.label);
        this.input.style.fontFamily = face.family;
        this.input.style.fontWeight = face.weight;
      }
      this.summary.textContent = this.t("lid", "Couvercle") + " " + v.lid +
        " · " + this.t("body", "Corps") + " " + v.body +
        (v.engraved
          ? " · " + this.t("text", "Texte") + " « " + v.text + " » " + v.textColor + ", " + v.font
          : " · " + this.t("no_text", "sans texte"));

      this.fields["Format"].value = FORMAT_LABEL[s.format];
      // mêmes noms stables que dans cartPayload : c'est la donnée de commande
      this.fields["Couvercle"].value = v.lidKey;
      this.fields["Corps"].value = v.bodyKey;
      this.fields["Texte"].value = v.text;
      this.fields["Couleur du texte"].value = v.engraved ? v.textColorKey : "";
      this.fields["Police"].value = v.engraved ? v.font : "";
      // an empty property would still show up in the cart, so switch it off
      this.fields["Texte"].disabled = !v.engraved;
      this.fields["Couleur du texte"].disabled = !v.engraved;
      this.fields["Police"].disabled = !v.engraved;

      if (this.cta) this.cta.href = this.ctaHref();

      if (this.variantInput) {
        const plain = this.getAttribute("variant-plain");
        const engraved = this.getAttribute("variant-engraved");
        this.variantInput.value = (v.engraved ? engraved : plain) || plain || engraved || "";
      }
    }

    /* ---------- rotation ---------- */

    _drag(e) {
      // la scène a son propre glissé, continu : celui-ci n'a plus d'objet
      if (this.scene3d) return;
      const t = e.touches ? e.touches[0] : e;
      const startX = t.clientX, startAngle = this.state.angle;
      const move = ev => {
        const p = ev.touches ? ev.touches[0] : ev;
        const step = Math.round((p.clientX - startX) / 34);
        const next = ((startAngle - step) % ANGLES + ANGLES) % ANGLES;
        if (next !== this.state.angle) { this.state.angle = next; this.paint(); }
        if (ev.cancelable) ev.preventDefault();
      };
      const up = () => {
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", up);
        window.removeEventListener("touchmove", move);
        window.removeEventListener("touchend", up);
      };
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
      window.addEventListener("touchmove", move, { passive: false });
      window.addEventListener("touchend", up);
    }

    /* ---------- painting ---------- */

    // grey-on-transparent render x flat colour = tinted part, alpha preserved
    _tint(name, hex) {
      const im = this._img(name);
      if (!im || !im.complete || !im.naturalWidth) return null;
      const off = document.createElement("canvas");
      off.width = im.naturalWidth; off.height = im.naturalHeight;
      const c = off.getContext("2d");
      c.drawImage(im, 0, 0);
      c.globalCompositeOperation = "multiply";
      c.fillStyle = hex;
      c.fillRect(0, 0, off.width, off.height);
      c.globalCompositeOperation = "destination-in";
      c.drawImage(im, 0, 0);
      return off;
    }

    /* Les reflets, ajoutés par-dessus la pièce teintée.

       Le teintage multiplie : sur une couleur sombre il ne reste presque rien
       du grain, parce que son amplitude suit la couleur — 11 % de modulation ne
       pèsent que 0,4 niveau de gris sur un noir mat. Un reflet, lui, s'ajoute.
       Il vaut autant sur un noir que sur un blanc, ce qui est exactement ce que
       fait la lumière sur une pointe de matière, et c'est ce qui donne sa
       texture aux teintes foncées.

       Le calque est blanc, son opacité porte la part claire du relief ; il est
       nul partout ailleurs, donc « lighter » n'ajoute rien hors des parois. */
    _sheen(ctx, name, sy, sh) {
      const im = this._img(name + "-sheen");
      if (!im || !im.complete || !im.naturalWidth) return;
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.globalAlpha = SHEEN;
      ctx.drawImage(im, 0, sy, TILE, sh, 0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.restore();
    }

    _engrave(ctx, quad, panel, k) {
      const s = this.state;
      const text = this._texteAffiche().trim();
      if (!text) return;
      const f = FONTS[s.font];
      const srcW = panel[0], srcH = panel[1];
      const a = (quad[1][0] - quad[0][0]) / srcW, b = (quad[1][1] - quad[0][1]) / srcW;
      const c = (quad[2][0] - quad[0][0]) / srcH, d = (quad[2][1] - quad[0][1]) / srcH;
      ctx.save();
      ctx.setTransform(a * k, b * k, c * k, d * k, quad[0][0] * k, quad[0][1] * k);
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      const REF = 100;
      if ("letterSpacing" in ctx) ctx.letterSpacing = (f.track * REF / 40) + "px";
      ctx.font = f.weight + " " + REF + "px " + f.family;
      const m = ctx.measureText(text);
      const refAsc = m.actualBoundingBoxAscent || REF * 0.72;
      const refDesc = m.actualBoundingBoxDescent || REF * 0.2;
      const size = Math.max(6, Math.min(srcW * 0.99 / m.width, srcH * 0.94 / (refAsc + refDesc)) * REF);
      if ("letterSpacing" in ctx) ctx.letterSpacing = (f.track * size / 40) + "px";
      ctx.font = f.weight + " " + size + "px " + f.family;
      const mm = ctx.measureText(text);
      const asc = mm.actualBoundingBoxAscent || size * 0.72;
      const desc = mm.actualBoundingBoxDescent || size * 0.2;
      const baseline = srcH / 2 + (asc - desc) / 2;
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = "#000000";
      ctx.fillText(text, srcW / 2, baseline + size * 0.035);
      ctx.globalAlpha = 0.96;
      ctx.fillStyle = PALETTE[this.state.letter].hex;
      ctx.fillText(text, srcW / 2, baseline);
      ctx.restore();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    paint() {
      const s = this.state;
      const lid = PALETTE[s.lid].hex, body = PALETTE[s.body].hex;

      /* Quand la scène est en place, elle possède le canvas principal. La vue
         de dessus reste en tuiles : elle est petite, fixe, et son grain est
         déjà gravé dedans — la passer en 3D n'apporterait rien. */
      if (this.scene3d) {
        this.scene3d.poser({
          format: s.format, corps: body, couvercle: lid,
          lettres: PALETTE[s.letter].hex, texte: this._texteAffiche(), police: s.font
        }).catch(() => {});
      } else if (this.canvas) {
        const ctx = this.canvas.getContext("2d");
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        // chaque pièce est teintée puis reçoit ses reflets, dans cet ordre :
        // une pièce dessinée ensuite recouvre proprement ce qui est dessous.
        [["body", body], ["lid", lid]].forEach(([kind, hex]) => {
          const name = s.format + "-" + kind + "-" + s.angle;
          const p = this._tint(name, hex);
          if (p) ctx.drawImage(p, 0, CROP_TOP, TILE, CROP_H, 0, 0, this.canvas.width, this.canvas.height);
          this._sheen(ctx, name, CROP_TOP, CROP_H);
        });
        const q = QUADS[s.format][s.angle].map(p => [p[0], p[1] - CROP_TOP]);
        this._engrave(ctx, q, PANEL_ANGLE, this.canvas.width / TILE);
      }

      if (this.topCanvas) {
        const ctx = this.topCanvas.getContext("2d");
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, this.topCanvas.width, this.topCanvas.height);
        [this._tint("top-body", body), this._tint("top-lid", lid)]
          .forEach(p => { if (p) ctx.drawImage(p, 0, 0, this.topCanvas.width, this.topCanvas.height); });
        this._engrave(ctx, TOP_QUAD, PANEL_TOP, this.topCanvas.width / TOP_W);
      }
    }
  }

  /* ----------------------------------------------------------------------
     <mwf-case-preview> — une image du résultat, sans aucun réglage.

     Sert de vignette dans le panier : le client y retrouve exactement l'étui
     qu'il a configuré. C'est une sous-classe du configurateur, pas une copie :
     le teintage (_tint), la projection de la gravure (_engrave) et paint() sont
     hérités tels quels, donc aucune constante de rendu n'est recopiée ici. Seule
     la mise en place change — un canvas, pas d'interface.

     Attributs : format, lid, body, text, text-color, font, angle.
     Les couleurs se donnent par leur nom de PALETTE, celui que les propriétés
     de ligne enregistrent.
     -------------------------------------------------------------------- */
  class MwfCasePreview extends MwfConfigurator {
    connectedCallback() {
      if (this._built) return;
      this._built = true;

      this.assets = this._readAssets();
      this.mesh3d = this._read3d();
      this.labels = this._readLabels();
      this.imgs = {};

      const fmt = this.getAttribute("format") === "120" ? "120" : "135";
      const angle = Math.min(Math.max(parseInt(this.getAttribute("angle") || "0", 10) || 0, 0), ANGLES - 1);
      const teinte = (attr, defaut) => {
        const i = this.colorIndex(this.getAttribute(attr));
        return i < 0 ? defaut : i;
      };
      const police = FONTS.findIndex(f =>
        f.label.toLowerCase() === String(this.getAttribute("font") || "").trim().toLowerCase());

      this.state = {
        format: fmt,
        lid: teinte("lid", 5),
        body: teinte("body", 0),
        letter: teinte("text-color", 3),
        font: police < 0 ? 0 : police,
        text: this.getAttribute("text") || "",
        angle: angle
      };

      this.canvas = el("canvas", "mwf-mini__canvas", this);
      this.canvas.width = TILE;
      this.canvas.height = CROP_H;
      this.topCanvas = null;

      this.setAttribute("role", "img");
      this.setAttribute("aria-label", this.t("preview_aria", "Aperçu de votre étui"));

      // deux tuiles suffisent — celles de cet angle — au lieu des seize
      this._img(fmt + "-body-" + angle);
      this._img(fmt + "-lid-" + angle);
      this.paint();
      if (document.fonts) {
        document.fonts.load("40px 'Blue Winter'").then(() => this.paint()).catch(() => {});
      }
    }

    // rien à synchroniser : ni formulaire, ni récapitulatif, ni bouton
    _sync() {}
  }

  function croix() {
    return '<svg class="mwf-croix" width="9" height="9" viewBox="0 0 9 9" aria-hidden="true">' +
      '<path d="M1 1 L8 8 M8 1 L1 8" stroke="currentColor" stroke-width="1.4" ' +
      'stroke-linecap="round" fill="none"/></svg>';
  }

  function arrow(right) {
    return right
      ? '<svg class="mwf-arrow" width="36" height="12" viewBox="0 0 36 12" aria-hidden="true"><rect x="0" y="5" width="28" height="2"/><path d="M36 6 L27 1.4 L27 10.6 Z"/></svg>'
      : '<svg class="mwf-arrow" width="36" height="12" viewBox="0 0 36 12" aria-hidden="true"><path d="M0 6 L9 1.4 L9 10.6 Z"/><rect x="8" y="5" width="28" height="2"/></svg>';
  }

  if (!window.customElements.get("mwf-configurator")) {
    window.customElements.define("mwf-configurator", MwfConfigurator);
  }
  if (!window.customElements.get("mwf-case-preview")) {
    window.customElements.define("mwf-case-preview", MwfCasePreview);
  }
})();
